import { RRecord } from "../../base/index.js";
import { isNonEmptyString } from "../../util/strings.js";
import { canonicalJSONStringify } from "../../util/canonicalize.js";
import { DeviceInboxBindingV1 } from "./DeviceInboxBindingV1.js";
import { isCanonicalDeviceId } from "./DeviceRegistrationV1.js";
import {
  requireCanonicalSpkiB64,
  isFiniteNumber,
  normalizeSig,
  validateEd25519Sig,
} from "./deviceRecordShared.js";
import { isCanonicalAccountCapabilityCertId } from "./accountCapabilityShared.js";

export const ACCOUNT_DEVICE_MUTATION_VERSION = 1;
export const ACCOUNT_DEVICE_MUTATION_PURPOSE = "rez:account-device-mutation:v1";

// The two mutation verbs. LOCAL to this record — NOT the capability vocabulary
// (though they share names with the capabilities that authorize them).
export const ACCOUNT_DEVICE_MUTATION_ACTIONS = Object.freeze(["device.add", "device.revoke"]);

/**
 * AccountDeviceMutationV1 (S2.5 S11) — a device's signed request to the account's
 * AUTHORITY HOME to mutate the account device set (add or revoke a sibling
 * device). The home serializes it under a per-account lock, folds canonical
 * state, and bumps a monotonic epoch.
 *
 * The submitter's per-op AUTHORITY is proven by the AUTHENTICATED session (the
 * home checks `sessionAuthority.grantedCapabilities` — device.add / device.revoke
 * — exactly as the shipped device.revoke handler does), so the envelope carries
 * NO cert chain: it is a bare signed statement bound to the session by
 * `signerPublicKeyB64` (B on a primary, C on a delegated device). `opId` is a
 * client-chosen idempotency key; `expectedRevision` is optimistic concurrency
 * against the home's current epoch (a mismatch returns the latest state, no
 * clobber).
 *
 * `target` is action-tagged:
 *   device.add    → { deviceInboxBinding: <DeviceInboxBindingV1 json> }
 *                   (the sibling's device-signed, self-certifying inbox+key proof.)
 *
 *                   FROZEN AND UNSAFE (audit #5, 2026-07-27). A required `deviceCapability` was
 *                   once added to this target while `v`/`purpose` stayed at 1/v1 — the signed
 *                   `target` is covered wholesale by signableBytes, so that silently redefined
 *                   what a v1 device.add signature means. The field is restored to V2, and a v1
 *                   device.add is REFUSED by the home with a typed upgrade-required error: without
 *                   the leaf cert the home cannot store a certId to auto-revoke, so the added
 *                   device's cert stays valid to off-home peers after a revoke (the audit R4
 *                   completeness blocker). It is parseable ONLY so that refusal can be specific.
 *
 *   device.revoke → { revokedDeviceId, revokedCertId? }
 *                   STILL SAFE AND STILL ACCEPTED at v1: its target shape never changed, only its
 *                   validation tightened (a `rez:dev:` prefix check became a canonical-hex check),
 *                   which narrows what is accepted and never broadens it. Old revoke signatures
 *                   still verify over the same bytes and still mean the same thing.
 *                   (revokedDeviceId self-cert. revokedCertId is NOT an arbitrary
 *                    add to the revoked-cert set: the home auto-revokes the target
 *                    device's OWN registry-bound cert, and a supplied revokedCertId is
 *                    only a redundant assertion that must EQUAL that bound cert — a
 *                    mismatch is rejected. Revoking an unrelated cert is the separate
 *                    capability.revoke operation, not this device-scoped mutation.)
 *
 * Signed body (everything except `sig`):
 *   { v, purpose, opId, accountIdentityPublicKeyB64, expectedRevision, action,
 *     target, signerPublicKeyB64, issuedAtMs, expiresAtMs }
 */
export class AccountDeviceMutationV1 extends RRecord {
  static type = "AccountDeviceMutationV1";

  constructor({
    v = ACCOUNT_DEVICE_MUTATION_VERSION,
    purpose = ACCOUNT_DEVICE_MUTATION_PURPOSE,
    opId,
    accountIdentityPublicKeyB64,
    expectedRevision,
    action,
    target,
    signerPublicKeyB64,
    issuedAtMs,
    expiresAtMs,
    sig,
  } = {}) {
    super();
    this.v = v;
    this.purpose = purpose;
    this.opId = opId;
    this.accountIdentityPublicKeyB64 = accountIdentityPublicKeyB64;
    this.expectedRevision = expectedRevision;
    this.action = action;
    this.target = target;
    this.signerPublicKeyB64 = signerPublicKeyB64;
    this.issuedAtMs = issuedAtMs;
    this.expiresAtMs = expiresAtMs;
    this.sig = normalizeSig(sig);
    this._seal();
  }

  validate() {
    this.assert(this.v === ACCOUNT_DEVICE_MUTATION_VERSION, "AccountDeviceMutationV1.v must be 1", { v: this.v });
    this.assert(this.purpose === ACCOUNT_DEVICE_MUTATION_PURPOSE, "AccountDeviceMutationV1.purpose must be " + ACCOUNT_DEVICE_MUTATION_PURPOSE, { purpose: this.purpose });
    this.assert(isNonEmptyString(this.opId), "AccountDeviceMutationV1.opId must be a non-empty string", { opId: this.opId });
    requireCanonicalSpkiB64(this.accountIdentityPublicKeyB64, "AccountDeviceMutationV1.accountIdentityPublicKeyB64");
    this.assert(
      Number.isInteger(this.expectedRevision) && this.expectedRevision >= 0,
      "AccountDeviceMutationV1.expectedRevision must be a non-negative integer",
      { expectedRevision: this.expectedRevision },
    );
    this.assert(
      ACCOUNT_DEVICE_MUTATION_ACTIONS.includes(this.action),
      'AccountDeviceMutationV1.action must be "device.add" or "device.revoke"',
      { action: this.action },
    );
    requireCanonicalSpkiB64(this.signerPublicKeyB64, "AccountDeviceMutationV1.signerPublicKeyB64");
    this.assert(isFiniteNumber(this.issuedAtMs), "AccountDeviceMutationV1.issuedAtMs must be number", { issuedAtMs: this.issuedAtMs });
    this.assert(isFiniteNumber(this.expiresAtMs), "AccountDeviceMutationV1.expiresAtMs must be number", { expiresAtMs: this.expiresAtMs });
    this.assert(this.expiresAtMs > this.issuedAtMs, "AccountDeviceMutationV1.expiresAtMs must be after issuedAtMs", { issuedAtMs: this.issuedAtMs, expiresAtMs: this.expiresAtMs });
    this.#validateTarget();
    validateEd25519Sig(this.sig, "AccountDeviceMutationV1.sig");
  }

  #validateTarget() {
    const target = this.target;
    this.assert(target && typeof target === "object" && !Array.isArray(target), "AccountDeviceMutationV1.target must be an object", { target });
    if (this.action === "device.add") {
      // The sibling's device-signed inbox binding — structurally validated by
      // constructing it (self-cert deviceId + SPKI + sig envelope pinned there).
      let binding;
      try {
        binding = new DeviceInboxBindingV1(target.deviceInboxBinding && typeof target.deviceInboxBinding === "object" ? target.deviceInboxBinding : {});
      } catch (err) {
        this.assert(false, "AccountDeviceMutationV1 device.add target.deviceInboxBinding is invalid: " + (err && err.message ? err.message : "unknown"), { target });
      }
      // Keep the constructed record from being GC-flagged as unused.
      this.assert(isNonEmptyString(binding.deviceId), "AccountDeviceMutationV1 device.add binding must carry a deviceId", { deviceId: binding.deviceId });

    } else {
      // device.revoke: a self-cert deviceId, and an optional revoked cert id. The
      // deviceId must have CANONICAL SYNTAX (`rez:dev:<64-lowercase-hex>`), not merely
      // a `rez:dev:` prefix. NOTE this proves SHAPE only, NOT `deviceId ===
      // deviceIdFor(pub)`: this record carries no pubkey for the revoke target, so it
      // cannot be key-proven here. The syntax guard still narrows the
      // forgeable-id space: the home writes a durable terminal tombstone for a revoke
      // target, and a loose prefix check would let a revoke-capable device mint
      // tombstones for arbitrary malformed strings. This is an upstream early-reject;
      // the registry independently enforces canonical shape as the invariant owner.
      // (audit R4 F1 — DoS-syntax guard.)
      this.assert(isNonEmptyString(target.revokedDeviceId), "AccountDeviceMutationV1 device.revoke target.revokedDeviceId must be a non-empty string", { target });
      this.assert(
        isCanonicalDeviceId(target.revokedDeviceId),
        "AccountDeviceMutationV1 device.revoke target.revokedDeviceId must be a canonical rez:dev:<64-hex> id",
        { revokedDeviceId: target.revokedDeviceId },
      );
      if (target.revokedCertId !== undefined && target.revokedCertId !== null) {
        // Audit R4 F3-remediation finding 2: EXACT canonical shape (rez:cap: + 64 lowercase
        // hex — what deriveAccountCapabilityCertId emits), not a bare prefix that would
        // accept `rez:cap:revoked-leaf` or any attacker-chosen string.
        this.assert(
          isCanonicalAccountCapabilityCertId(target.revokedCertId),
          "AccountDeviceMutationV1 device.revoke target.revokedCertId must be a canonical rez:cap:<64-hex> id or omitted",
          { revokedCertId: target.revokedCertId },
        );
      }
    }
  }

  /**
   * The exact bytes the signer signs and the home recomputes — the signed body
   * minus `sig`. Deterministic via canonical JSON (canonicalizes the nested
   * target too).
   */
  static signableBytes({ v, purpose, opId, accountIdentityPublicKeyB64, expectedRevision, action, target, signerPublicKeyB64, issuedAtMs, expiresAtMs } = {}) {
    const body = { v, purpose, opId, accountIdentityPublicKeyB64, expectedRevision, action, target, signerPublicKeyB64, issuedAtMs, expiresAtMs };
    return new TextEncoder().encode(canonicalJSONStringify(body));
  }
}
